import { Component, computed, signal, Output, EventEmitter } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { WorkflowStatus } from '@modules/campaigns/Domain/Enums/WorkflowStatus';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { MessagesModule } from 'primeng/messages';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';

import { ConditionalNode } from '@modules/campaigns/Domain/Nodes/ConditionalNode';
import {
  getValidationErrorMessage,
  ValidationError,
} from '@modules/campaigns/Domain/Enums/ValidationErrors';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';
import { ConditionalConfig, SMSConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { forkJoin, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-conditional-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    LucideIconsModule,
    MessagesModule,
    NgClass,
    TooltipModule,
    DropdownModule,
  ],
  templateUrl: './conditional-inspector.component.html',
  styleUrls: ['./conditional-inspector.component.scss'],
})
export class ConditionalInspectorComponent extends InspectorBase<ConditionalNode> {
  @Output() nodeGoalToSelect = new EventEmitter<void>();

  selectedTargetNodeId = signal<string | null>(null);
  selectedStatuses = signal<string[]>([]);
  targetNodeOptions = signal<NodeClass[]>([]);
  targetNodeCopies = signal<Record<string, CopyModel | undefined>>({});

  isNodeGoalDisabled = computed(() => {
    const workflow = this.workflowState.workflow();
    if (!workflow) return false;
    return (
      workflow.status === WorkflowStatus.PUBLISHED ||
      workflow.status === WorkflowStatus.PAUSED ||
      workflow.status === WorkflowStatus.UNPUBLISHED
    );
  });

  nodeGoalWarning = computed(() => {
    const workflow = this.workflowState.workflow();
    if (!workflow || !this.isNodeGoalDisabled()) return null;

    let statusLabel = '';
    switch (workflow.status) {
      case WorkflowStatus.PUBLISHED:
        statusLabel = 'PUBLISHED';
        break;
      case WorkflowStatus.PAUSED:
        statusLabel = 'PAUSED';
        break;
      default:
        return null;
    }

    return `El nodo objetivo no se puede cambiar porque el estado de la campaña se encuentra en ${statusLabel}`;
  });

  statusOptions = [
    {
      label: 'received',
      value: 'received',
      icon: 'pi pi-check-circle',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-100',
    },
    {
      label: 'read',
      value: 'read',
      icon: 'pi pi-eye',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
    },
    {
      label: 'error',
      value: 'error',
      icon: 'pi pi-exclamation-circle',
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
    },
  ];

  selectedTargetNode = computed(() => {
    const id = this.selectedTargetNodeId();
    if (!id) return null;
    return this.targetNodeOptions().find((n) => n.id === id) ?? null;
  });

  previewMessage = computed(() => {
    const node = this.selectedTargetNode();
    if (!node) return '';

    const copy = this.targetNodeCopies()[node.id];
    if (!copy) return '';

    const textBlock = copy.blocks?.find(
      (b) =>
        b.type === 'smsText' ||
        b.type === 'text' ||
        b.alias === 'smsText' ||
        b.type === 'certifiedSmsText',
    );
    if (!textBlock) return '';

    return textBlock.translations?.[0]?.text ?? '';
  });

  previewSummary = computed(() => {
    const node = this.selectedTargetNode();
    const statuses = this.selectedStatuses();

    if (!node || statuses.length === 0) return '';

    const nodeName = this.getNodeTypeLabel(node.type);
    const statusLabels = statuses
      .map((s) =>
        this.translate.instant(
          `DATA.MODULES.INSPECTORS.CONDITIONAL.CONFIG.STATUS_${s.toUpperCase()}`,
        ),
      )
      .join(', ');

    return this.translate.instant('DATA.MODULES.INSPECTORS.CONDITIONAL.PREVIEW.SUMMARY_TEXT', {
      nodeName: nodeName,
      statuses: statusLabels,
      truePath: this.translate.instant('DATA.MODULES.INSPECTORS.CONDITIONAL.CONFIG.TRUE_CASE'),
      falsePath: this.translate.instant('DATA.MODULES.INSPECTORS.CONDITIONAL.CONFIG.FALSE_CASE'),
    });
  });

  constructor(
    private workflowState: WorkflowStateService,
    private translate: TranslateService,
    private copiesService: CopiesService,
  ) {
    super();
  }

  getErrorMessage(error: NodeValidationError): string {
    return getValidationErrorMessage(
      error.errorMessage as ValidationError,
      error.params,
      this.translate,
    );
  }

  override get nodeValue(): ConditionalNode | null {
    const current = this.node();
    return current instanceof ConditionalNode ? current : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.CONDITIONAL.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.CONDITIONAL.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  loadOptions() {
    const currentNode = this.node();
    if (currentNode) {
      const workflow = this.workflowState.workflow();
      if (workflow) {
        const parents = workflow.getParentNodesWithCopyIdsInBranch(currentNode);
        this.targetNodeOptions.set(parents);

        const cfg = currentNode.config as ConditionalConfig;
        if (!cfg.targetNodeId) {
          this.workflowState.setHighlightedNode(null);
        }

        const copyRequests = parents
          .map((node) => {
            const config = node.config as SMSConfig;
            const copyId = config['copyId'] || (config.copyIds && config.copyIds[0]);
            if (copyId) {
              return this.copiesService.getCopy(copyId).pipe(
                map((res) => ({ nodeId: node.id, copy: res.data.copy })),
                catchError(() => of({ nodeId: node.id, copy: null })),
              );
            }
            return of(null);
          })
          .filter((req) => req !== null);

        if (copyRequests.length > 0) {
          forkJoin(copyRequests).subscribe((results) => {
            const copiesMap: Record<string, CopyModel | undefined> = { ...this.targetNodeCopies() };
            results.forEach((res: any) => {
              if (res && res.copy) {
                copiesMap[res.nodeId] = res.copy;
              }
            });
            this.targetNodeCopies.set(copiesMap);
          });
        }
      }
    }
  }

  onTargetNodeSelect(nodeId: string | null) {
    if (this.isNodeGoalDisabled()) return;

    this.selectedTargetNodeId.set(nodeId);
    this.workflowState.setHighlightedNode(nodeId ?? null);
    this.scheduleApplyChanges();
  }

  getNodeMessage(nodeId: string): string {
    const copy = this.targetNodeCopies()[nodeId];
    if (!copy) return '';

    const textBlock = copy.blocks?.find(
      (b) =>
        b.type === 'smsText' ||
        b.type === 'text' ||
        b.alias === 'smsText' ||
        b.type === 'certifiedSmsText',
    );
    return textBlock?.translations?.[0]?.text ?? '';
  }

  getNodeMessagePreview(nodeId: string, maxLength = 30): string {
    const message = this.getNodeMessage(nodeId);
    if (!message) return '';

    const normalized = message.trim();
    if (normalized.length <= maxLength) return normalized;

    return `${normalized.slice(0, maxLength)}...`;
  }

  getNodeTypeLabel(type: NodeType): string {
    switch (type) {
      case NodeType.SendSMS:
        return 'SMS';
      case NodeType.SendEmail:
        return 'EMAIL';
      case NodeType.SendCertifiedSMS:
        return 'CERTIFIED SMS';
      case NodeType.SendCertifiedEmail:
        return 'CERTIFIED EMAIL';
      default:
        return 'NODE';
    }
  }

  onStatusToggle(status: string) {
    const current = this.selectedStatuses();
    if (current.includes(status)) {
      this.selectedStatuses.set(current.filter((s) => s !== status));
    } else {
      this.selectedStatuses.set([...current, status]);
    }
    this.scheduleApplyChanges();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const baseConfig = (node.config as ConditionalConfig) ?? {};

    const updatedConfig: ConditionalConfig = {
      ...baseConfig,
      targetNodeId: this.selectedTargetNodeId(),
      statuses: this.selectedStatuses().length > 0 ? this.selectedStatuses() : null,
    };

    const updated = node.cloneWithConfig(updatedConfig) as ConditionalNode;
    const validation = updated.validate();
    this.errors.set(validation);
    const shouldPersist =
      validation.length === 0 ||
      validation.every((error) => error.errorMessage === ValidationError.NO_CASE_CONDITIONAL);

    if (shouldPersist) {
      this.workflowState.addOrUpdateNode(updated);
    }
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = node.config as ConditionalConfig;
    this.selectedTargetNodeId.set(cfg.targetNodeId ?? null);
    if (cfg.targetNodeId) {
      this.workflowState.setHighlightedNode(cfg.targetNodeId);
    }
    this.selectedStatuses.set(cfg.statuses ?? []);
    this.errors.set([]);
  }

  override setNode(nodeSignal: any) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
    this.loadOptions();
  }

  getNodeIcon(type: NodeType): string {
    switch (type) {
      case NodeType.SendSMS:
      case NodeType.SendCertifiedSMS:
        return 'pi-mobile';
      case NodeType.SendEmail:
      case NodeType.SendCertifiedEmail:
        return 'pi-envelope';
      default:
        return 'pi-info-circle';
    }
  }
}
